import { Injectable } from '@angular/core';
import { Observable, of, ReplaySubject, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { AuthenticateModel, AuthenticationModel, AuthenticationService as ApiAuthenticationService, RefreshAuthenticationModel, Rights } from './api-services';
import { UniversalStorage } from '../storages/universal.storage';
import { LocalStorage } from '../storages/local.storage';

@Injectable({
  providedIn: 'root'
})
export class AuthenticationService {
  private static readonly authenticationKey = 'bn.authentication';
  private authenticatedModel: AuthenticationModel | null = null;
  private tokenExpirationDate: number | null = null;
  private refreshTokenExpirationDate: number | null = null;
  private hasToMfa: boolean = false;
  public $authenticated = new ReplaySubject<AuthenticationModel | null>(1);

  constructor(private readonly apiAuthenticationService: ApiAuthenticationService,
    private localStorage: LocalStorage,
    private readonly universalStorage: UniversalStorage) {
    const authenticatedModel = this.universalStorage.getItemFromObject<AuthenticationModel>(AuthenticationService.authenticationKey);

    this.setLoginInfo(authenticatedModel);
  }

  public setLoginInfo(data: AuthenticationModel | null): void {
    this.authenticatedModel = data;

    if (data) {
      this.universalStorage.setItemFromObject<AuthenticationModel>(AuthenticationService.authenticationKey, data);
      this.tokenExpirationDate = this.getExpirationDateFromToken(data.token);
      this.hasToMfa = data.hasToMfa;
      if (!data.hasToMfa) {
        this.refreshTokenExpirationDate = this.getExpirationDateFromToken(data.refreshToken);
      }
      else {
        this.refreshTokenExpirationDate = null;
      }
    }
    else {
      // remove the items if data was null or undefined
      this.universalStorage.removeItem(AuthenticationService.authenticationKey);
      this.tokenExpirationDate = null;
      this.refreshTokenExpirationDate = null;
    }

    this.$authenticated.next(this.authenticatedModel);
  }

  public logout(): void {
    this.setLoginInfo(null);
  }

  public getLoginUrl(): string {
    return 'account/login';
  }

  public getLoginAuthorizationWallUrl(): string {
    return 'account/login-authorization-wall';
  }

  public getMfaUrl(): string {
    return 'account/authenticate-by-app';
  }

  public getToken(): string | null {
    return this.authenticatedModel?.token ?? null;
  }

  public getRefreshToken(): string | null {
    return this.authenticatedModel?.refreshToken ?? null;
  }

  public tokenExpired(): boolean {
    if (!this.tokenExpirationDate) {
      return true;
    }

    return this.isExpired(this.tokenExpirationDate);
  }

  private refreshTokenExpired(): boolean {
    if (!this.refreshTokenExpirationDate) {
      return true;
    }

    return this.isExpired(this.refreshTokenExpirationDate);
  }

  private getExpirationDateFromToken(token: string): number {
    return JSON.parse(this.b64decode(token.split('.')[1])).exp;
  }

  private isExpired(expirationDate: number): boolean {
    return Math.floor(new Date().getTime() / 1000) >= expirationDate;
  }

  private b64decode(str: string): string {
    let chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/=';
    let output: string = '';

    str = String(str).replace(/=+$/, '');

    if (str.length % 4 == 1) {
      throw new Error("'atob' failed: The string to be decoded is not correctly encoded.");
    }

    for (
      // initialize result and counters
      let bc: number = 0, bs: any, buffer: any, idx: number = 0;
      // get next character
      buffer = str.charAt(idx++);
      // character found in table? initialize bit storage and add its ascii value;
      ~buffer && (bs = bc % 4 ? bs * 64 + buffer : buffer,
        // and if not first of each 4 characters,
        // convert the first 8 bits to one ascii character
        bc++ % 4) ? output += String.fromCharCode(255 & bs >> (-2 * bc & 6)) : 0
    ) {
      // try to find character in table (0-63, not found => -1)
      buffer = chars.indexOf(buffer);
    }
    return output;
  }

  public getDisplayName(): string | null {
    return this.authenticatedModel?.displayName ?? null;
  }

  public getLastName(): string | null {
    return this.authenticatedModel?.lastname ?? null;
  }

  public getFirstName(): string | null {
    return this.authenticatedModel?.firstname ?? null;
  }

  public getEmail(): string | null {
    return this.authenticatedModel?.email ?? null;
  }

  public getUserId(): string | null {
    return this.authenticatedModel?.userId ?? null;
  }

  public getRoles(): string[] | null {
    return this.authenticatedModel?.roleKeys ?? null;
  }

  public getRights(): Rights[] | null {
    return this.authenticatedModel?.rightKeys ?? null;
  }

  public hasRight(right: Rights): boolean {
    if (!this.authenticatedModel) {
      return false;
    }

    return this.authenticatedModel.rightKeys.includes(right);
  }

  public hasAnyRight(rights: Rights[]): boolean {
    if (!this.authenticatedModel) {
      return false;
    }

    return this.authenticatedModel.rightKeys.some(right => rights.includes(right));
  }

  public isSystemUser(): boolean {
    return this.authenticatedModel?.isSystemUser ?? false;
  }

  public isAuthenticated(): boolean {
    return !this.tokenExpired();
  }

  authenticate(loginName: string, password: string): Observable<AuthenticationModel | null> {
    let rememberMeToken = this.getRememberMeToken(loginName);

    const model: AuthenticateModel = {
      loginName: loginName,
      password: password,
      rememberMeToken: rememberMeToken
    };

    if (model) {
      return this.apiAuthenticationService.authenticate(model)
        .pipe(tap((result: AuthenticationModel | null) => {
          return result;
        }));
    }
    else {
      return of(null)
    }
  }

  public getRememberMeToken(loginName: string): string | null {
    let key = this.hashLoginName(loginName);

    return this.localStorage.getItem(key);
  }

  public setRememberMeToken(loginName: string, value: string): void {
    let key = this.hashLoginName(loginName);

    this.localStorage.setItem(key, value);
  }

  public removeRememberMeToken(loginName: string): void {
    let key = this.hashLoginName(loginName);

    this.localStorage.removeItem(key);
  }

  public setToken(token: string): void {
    let authenticatedModel = this.universalStorage.getItemFromObject<AuthenticationModel>(AuthenticationService.authenticationKey);

    if (authenticatedModel) {
      authenticatedModel.token = token;

      this.setLoginInfo(authenticatedModel);
    }
  }

  public getHasToMfa(): boolean {
    return this.hasToMfa;
  }

  public getIsTwoFactorActive(): boolean {
    return this.authenticatedModel?.twoFactorActive ?? false;
  }

  refreshToken(): Observable<AuthenticationModel | null> {
    const refreshToken = this.getRefreshToken();

    if (refreshToken) {
      if (this.refreshTokenExpired()) {
        this.logout();
        return of(null);
      }

      const refreshModel: RefreshAuthenticationModel = {
        refreshToken: refreshToken
      };

      return this.apiAuthenticationService.refreshToken(refreshModel)
        .pipe(tap((result: AuthenticationModel | null) => {
          this.setLoginInfo(result);

          return result;
        }),
          catchError(error => {
            // Logout if the refresh token was not valid
            this.logout();

            return throwError(error);
          })
        );
    }
    else {
      return of(null)
    }
  }

  public hashLoginName(userName: string): string {
    let hash = 0, i, chr, stringHash = '';
    if (userName.length === 0) return stringHash;
    for (i = 0; i < userName.length; i++) {
      chr = userName.charCodeAt(i);
      hash = (hash << 5) - hash + chr;
      hash |= 0; // Convert to 32bit integer
      // to reduce chance to have same hash twice on one device
      if (i < 3) {
        stringHash += chr;
      }
    }

    stringHash += hash.toString();

    return stringHash;
  }
}
